/**
 * useMessageDetails - React Query hooks for message thread details and replies
 *
 * Split into two separate APIs:
 * 1. useThreadDetails - Fetch thread metadata (participants, subject, etc.)
 * 2. useThreadReplies - Fetch paginated replies with infinite scroll
 * 3. useThreadDetailsAndReplies - Combined hook for convenience
 */

import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { fetchThreadDetails, fetchThreadReplies } from '../services/messageService';
import { messageQueryKeys } from '../config/queryKeys';
import { queryClient } from 'config/queryClient';

// ─── Thread Details (Metadata Only) ───────────────────────────────────────────

/**
 * Fetch thread metadata (participants, subject, case info, etc.)
 * @param {string} messageThreadId - UUID of the message thread
 * @param {Object} options - { enabled }
 * @returns {{ data, isLoading, error, refetch }}
 */
export function useThreadDetails(messageThreadId, options = {}) {
  const { enabled = true, excludeFromLoading = false } = options;

  const query = useQuery({
    queryKey: messageQueryKeys.threadDetails.byId(messageThreadId),
    queryFn: () => fetchThreadDetails(messageThreadId, excludeFromLoading),
    enabled: Boolean(messageThreadId) && enabled,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    select: (response) => response?.data || null,
  });

  return query;
}

// ─── Thread Replies (Paginated with Infinite Scroll) ─────────────────────────

/**
 * Fetch paginated replies with infinite scroll support
 * @param {string} messageThreadId - UUID of the message thread
 * @param {Object} options - { enabled, pageSize }
 * @returns {{
 *   data: { pages, pageParams },
 *   replies: Array,
 *   totalCount: number,
 *   isLoading,
 *   isFetchingNextPage,
 *   hasNextPage,
 *   fetchNextPage,
 *   refetch
 * }}
 */
export function useThreadReplies(messageThreadId, options = {}) {
  const { enabled = true, pageSize = 50 } = options;

  const query = useInfiniteQuery({
    queryKey: messageQueryKeys.threadReplies.byId(messageThreadId),
    queryFn: ({ pageParam = 0 }) =>
      fetchThreadReplies({
        messageThreadId,
        limit: pageSize,
        offset: pageParam,
        excludeFromLoading: pageParam > 0,
      }),
    enabled: Boolean(messageThreadId) && enabled,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = (allPages.length - 1) * pageSize;
      const totalLoaded = allPages.reduce((sum, page) => sum + (page?.data?.replies?.length || 0), 0);
      const totalCount = lastPage?.data?.count || 0;

      // If we've loaded all replies, return undefined to indicate no more pages
      if (totalLoaded >= totalCount) {
        return undefined;
      }

      // Return next offset
      return currentOffset + pageSize;
    },
    select: (data) => {
      // Flatten all pages into a single array of replies
      const allReplies = data.pages.flatMap((page) => page?.data?.replies || []);
      const totalCount = data.pages[0]?.data?.count || 0;

      return {
        pages: data.pages,
        pageParams: data.pageParams,
        replies: allReplies,
        totalCount,
      };
    },
  });

  // Flatten replies for easier consumption
  const replies = useMemo(() => query.data?.replies || [], [query.data]);
  const totalCount = useMemo(() => query.data?.totalCount || 0, [query.data]);

  return {
    ...query,
    replies,
    totalCount,
  };
}

// ─── Combined Hook (Using Both APIs) ──────────────────────────────────────────

/**
 * Fetch both thread details and replies
 * @param {string} messageThreadId - UUID of the message thread
 * @param {Object} options - { enabled, pageSize }
 * @returns {{
 *   threadDetails: Object,
 *   replies: Array,
 *   totalCount: number,
 *   isLoadingDetails,
 *   isLoadingReplies,
 *   isFetchingNextPage,
 *   hasNextPage,
 *   fetchNextPage,
 *   refetchDetails,
 *   refetchReplies,
 *   refetchAll
 * }}
 */
export function useThreadDetailsAndReplies(messageThreadId, options = {}) {
  const { enabled = true, pageSize = 10 } = options;

  const threadDetails = queryClient.getQueryData(messageQueryKeys.threadDetails.byId(messageThreadId));

  const detailsQuery = useThreadDetails(messageThreadId, { enabled, excludeFromLoading: !threadDetails?.data });
  const repliesQuery = useThreadReplies(messageThreadId, { enabled, pageSize });

  const refetchAll = useCallback(() => {
    detailsQuery.refetch();
    repliesQuery.refetch();
  }, [detailsQuery, repliesQuery]);

  return {
    // Thread details
    threadDetails: detailsQuery.data,
    isLoadingDetails: detailsQuery.isLoading,
    isErrorDetails: detailsQuery.isError,
    errorDetails: detailsQuery.error,
    refetchDetails: detailsQuery.refetch,

    // Thread replies
    replies: repliesQuery.replies,
    totalCount: repliesQuery.totalCount,
    isLoadingReplies: repliesQuery.isLoading,
    isFetchingNextPage: repliesQuery.isFetchingNextPage,
    hasNextPage: repliesQuery.hasNextPage,
    fetchNextPage: repliesQuery.fetchNextPage,
    refetchReplies: repliesQuery.refetch,

    // Combined
    isLoading: detailsQuery.isLoading || repliesQuery.isLoading,
    refetchAll,
  };
}
// ─── Invalidation Helpers ────────────────────────────────────────────────────

/**
 * Hook to invalidate and refetch thread details
 */
export function useInvalidateThreadDetails() {
  const queryClient = useQueryClient();

  return useCallback(
    (messageThreadId) => {
      if (messageThreadId) {
        queryClient.invalidateQueries({ queryKey: messageQueryKeys.threadDetails.byId(messageThreadId) });
      } else {
        queryClient.invalidateQueries({ queryKey: messageQueryKeys.threadDetails.all });
      }
    },
    [queryClient]
  );
}

/**
 * Hook to invalidate and refetch thread replies
 */
export function useInvalidateThreadReplies() {
  const queryClient = useQueryClient();

  return useCallback(
    (messageThreadId) => {
      if (messageThreadId) {
        queryClient.invalidateQueries({ queryKey: messageQueryKeys.threadReplies.byId(messageThreadId) });
      } else {
        queryClient.invalidateQueries({ queryKey: messageQueryKeys.threadReplies.all });
      }
    },
    [queryClient]
  );
}
